// Global search — Ctrl/⌘+K palette across guests, bookings, rooms, invoices, logbook.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { BedDouble, BookOpen, Brain, DoorOpen, Loader2, Package, Receipt, Search, User, X } from 'lucide-react';
import { apiCall } from './_shared';

const ICONS = { booking: User, guest: User, memory: Brain, room: BedDouble, unit: DoorOpen, invoice: Receipt, logbook: BookOpen, lostfound: Package };
const LABELS = { booking: 'Booking', guest: 'Guest', memory: 'Memory', room: 'Room type', unit: 'Room', invoice: 'Invoice', logbook: 'Logbook', lostfound: 'Lost & found' };

const CommandPalette = ({ onNavigate }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [idx, setIdx] = useState(0);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const timer = useRef(null);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); setOpen((o) => !o); }
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    const onOpen = () => setOpen(true);
    window.addEventListener('open-command-palette', onOpen);
    return () => { window.removeEventListener('keydown', onKey); window.removeEventListener('open-command-palette', onOpen); };
  }, []);

  useEffect(() => {
    if (open) { setTimeout(() => inputRef.current?.focus(), 30); }
    else { setQ(''); setResults([]); setIdx(0); }
  }, [open]);

  useEffect(() => {
    clearTimeout(timer.current);
    if (q.trim().length < 2) { setResults([]); return; }
    timer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await apiCall(`/api/admin/search?q=${encodeURIComponent(q.trim())}`);
        setResults(r.results || []); setIdx(0);
      } catch (_) { setResults([]); }
      finally { setLoading(false); }
    }, 220);
    return () => clearTimeout(timer.current);
  }, [q]);

  const go = useCallback((r) => { if (!r) return; setOpen(false); onNavigate?.(r.target, r); }, [onNavigate]);

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setIdx((i) => Math.min(i + 1, results.length - 1)); }
    if (e.key === 'ArrowUp') { e.preventDefault(); setIdx((i) => Math.max(i - 1, 0)); }
    if (e.key === 'Enter') { e.preventDefault(); go(results[idx]); }
  };

  if (!open) return null;

  const rows = results.map((r, i) => {
    const Icon = ICONS[r.type] || Search;
    return (
      <li key={`${r.type}-${r.id}`}>
        <button
          onMouseEnter={() => setIdx(i)}
          onClick={() => go(r)}
          className={`w-full text-left px-3 py-2 flex items-center gap-3 rounded-lg ${i === idx ? 'bg-orange-50' : 'hover:bg-slate-50'}`}
          data-testid={`search-result-${r.type}-${r.id}`}
        >
          <Icon className={`w-4 h-4 flex-shrink-0 ${i === idx ? 'text-orange-500' : 'text-slate-400'}`} />
          <span className="flex-1 min-w-0">
            <span className="block text-sm font-medium text-slate-900 truncate">{r.title}</span>
            <span className="block text-xs text-slate-500 truncate">{r.subtitle}</span>
          </span>
          <span className="text-[10px] uppercase tracking-wider text-slate-400 flex-shrink-0">{LABELS[r.type] || r.type}</span>
        </button>
      </li>
    );
  });

  return (
    <div className="fixed inset-0 z-[70] bg-slate-900/50 backdrop-blur-sm flex items-start justify-center pt-[12vh] px-4" onClick={() => setOpen(false)} data-testid="command-palette">
      <div className="bg-white w-full max-w-xl rounded-2xl shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 px-4 py-3 border-b">
          {loading ? <Loader2 className="w-4 h-4 animate-spin text-orange-500" /> : <Search className="w-4 h-4 text-slate-400" />}
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Search guests, bookings, room numbers, invoices, notes…"
            className="flex-1 outline-none text-sm py-1"
            data-testid="command-palette-input"
          />
          <kbd className="text-[10px] text-slate-400 border rounded px-1.5 py-0.5">ESC</kbd>
          <button onClick={() => setOpen(false)} className="p-1 rounded hover:bg-slate-100 sm:hidden"><X className="w-4 h-4" /></button>
        </div>
        <ul className="max-h-[50vh] overflow-y-auto p-2" data-testid="command-palette-results">
          {rows.length ? rows : (
            <li className="px-3 py-6 text-center text-xs text-slate-400">
              {q.trim().length < 2 ? 'Type at least 2 characters — try a guest name, a room number like 716, or an invoice number.' : loading ? 'Searching…' : 'No matches.'}
            </li>
          )}
        </ul>
        <div className="px-4 py-2 border-t text-[11px] text-slate-400 flex gap-3"><span>↑↓ navigate</span><span>↵ open</span><span>Ctrl/⌘+K toggle</span></div>
      </div>
    </div>
  );
};

export default CommandPalette;
